import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { useNotification } from '../components/Notification';
import { formatUsdAmount } from '../utils/money';
import { ensureGasOrDrip, isGasDripBlocked, notifyGasDripActionCompleted } from '../utils/gasDrip';
import { linkWalletWithProof, isWalletLimitError } from '../utils/walletAuth';
import { claimNftRewardWithAuth, getNftRewardContractAddress } from '../utils/nftRewardChain';
import { syncChainConfigFromServer } from '../utils/syncChainConfig';

function fmtUtc(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-GB', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }) + ' UTC';
  } catch {
    return String(d);
  }
}

function shortAddr(a) {
  if (!a) return '';
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

const Reward = () => {
  const { user } = useAuth();
  const { account, ensureConnected } = useWallet();
  const { showNotification } = useNotification();
  const [stats, setStats] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);
  const [walletFilter, setWalletFilter] = useState('');

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/nft-rewards/stats');
      setStats(data);
    } catch {
      setStats(null);
    }
  }, []);

  const loadMe = useCallback(async () => {
    if (!user) {
      setMe(null);
      return;
    }
    try {
      const params = walletFilter ? { wallet: walletFilter } : {};
      const { data } = await api.get('/nft-rewards/me', { params });
      setMe(data);
      if (!walletFilter && data.wallet) setWalletFilter(data.wallet);
    } catch (e) {
      console.error(e);
      setMe(null);
    }
  }, [user, walletFilter]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await syncChainConfigFromServer();
      } catch {
        /* ignore */
      }
      await loadStats();
      setLoading(false);
    })();
  }, [loadStats]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const sharePct = useMemo(() => {
    const mine = me?.openPoints?.points || 0;
    const total = me?.openPoints?.platformTotalPoints || 0;
    if (!total) return 0;
    return (mine / total) * 100;
  }, [me]);

  const handleClaim = async (item) => {
    if (claimingId) return;
    setClaimingId(String(item.allocationId));
    let walletAddress = account;
    try {
      if (!walletAddress) walletAddress = await ensureConnected();
      walletAddress = String(walletAddress).toLowerCase();
      if (walletAddress !== String(item.walletAddress || me?.wallet || '').toLowerCase()) {
        // claim for the allocation wallet — must match linked
      }
      try {
        await linkWalletWithProof(walletAddress);
      } catch (e) {
        if (!isWalletLimitError(e)) throw e;
      }

      const linked = walletAddress;
      const ok = await ensureGasOrDrip(linked, {
        showNotification,
        label: 'NFT reward claim',
        playType: 'boost',
        purpose: 'nft_reward_claim',
      });
      if (!ok) return;

      if (!getNftRewardContractAddress()) {
        await syncChainConfigFromServer();
      }

      const { data: auth } = await api.post('/nft-rewards/claim/authorization', {
        walletAddress: linked,
        distributionId: item.distributionId,
        allocationId: item.allocationId,
      });

      let txHash;
      try {
        txHash = await claimNftRewardWithAuth(
          auth.amountWei,
          auth.chainDistributionId,
          auth.nonce,
          auth.deadline,
          auth.signature
        );
      } catch (txErr) {
        await api.post('/nft-rewards/claim/release', {
          distributionId: item.distributionId,
          allocationId: item.allocationId,
        });
        throw txErr;
      }

      await api.post('/nft-rewards/claim/confirm', {
        walletAddress: linked,
        distributionId: item.distributionId,
        allocationId: item.allocationId,
        txHash,
      });
      await notifyGasDripActionCompleted({
        walletAddress: linked,
        txHash,
        playType: 'boost',
        purpose: 'nft_reward_claim',
        label: 'NFT reward claim',
      });
      showNotification(`Claimed ${formatUsdAmount(item.amountUsd)} USDC`, 'success');
      await Promise.all([loadMe(), loadStats()]);
    } catch (error) {
      if (!isGasDripBlocked(error)) {
        showNotification(
          error?.response?.data?.message || error.message || 'Claim failed',
          'error'
        );
      }
    } finally {
      setClaimingId(null);
    }
  };

  const claimAll = async () => {
    const items = me?.claimable?.items || [];
    for (const item of items) {
      // sequential to avoid nonce races
      // eslint-disable-next-line no-await-in-loop
      await handleClaim({ ...item, walletAddress: me.wallet });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-300">
        Loading rewards…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_#1e293b_0%,_#020617_55%)] text-slate-100">
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0ibm9uZSIvPjxwYXRoIGQ9Ik0zMCAwVjYwTTAgMzBINjAiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNnKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-60" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <p className="text-amber-400/90 text-xs font-semibold tracking-[0.2em] uppercase mb-2">
                Holder revenue share
              </p>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-white">
                NFT Rewards
              </h1>
              <p className="mt-3 max-w-xl text-slate-400 text-sm sm:text-base">
                Hold eligible NFTs through monthly snapshots to earn points. When revenue is
                distributed, claim your USDC share on-chain — only your linked wallet can unlock it.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full lg:w-auto">
              {[
                { label: 'Distributed', value: formatUsdAmount(stats?.totalDistributedUsd || 0) },
                { label: 'Claimed', value: formatUsdAmount(stats?.totalClaimedUsd || 0) },
                { label: 'Open points', value: String(stats?.openPoints ?? 0) },
                {
                  label: 'Next snapshot',
                  value: stats?.nextSnapshotAt
                    ? new Date(stats.nextSnapshotAt).toLocaleDateString('en-GB', { timeZone: 'UTC' })
                    : 'TBD',
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur px-4 py-3 min-w-[7.5rem]"
                >
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.label}</div>
                  <div className="mt-1 text-lg font-bold text-amber-300 tabular-nums">{s.value}</div>
                </div>
              ))}
            </div>
          </div>

          {!user ? (
            <div className="rounded-3xl border border-amber-500/30 bg-amber-500/10 p-8 text-center">
              <p className="text-lg font-semibold text-amber-100">Sign in & connect a wallet</p>
              <p className="mt-2 text-sm text-amber-100/70">
                Link your wallet to track holdings, snapshot points, and claim USDC rewards.
              </p>
              <Link
                to="/login"
                className="inline-flex mt-5 rounded-xl bg-amber-400 px-5 py-2.5 text-sm font-bold text-slate-950 hover:bg-amber-300 transition"
              >
                Sign in
              </Link>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Wallet + live holdings */}
              <section className="rounded-3xl border border-white/10 bg-slate-900/60 p-6 sm:p-8">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                  <h2 className="text-xl font-bold text-white">Your position</h2>
                  {(me?.wallets || []).length > 1 && (
                    <select
                      value={walletFilter}
                      onChange={(e) => setWalletFilter(e.target.value)}
                      className="rounded-lg bg-slate-800 border border-white/10 text-sm px-3 py-2"
                    >
                      {me.wallets.map((w) => (
                        <option key={w} value={w}>
                          {shortAddr(w)}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div className="rounded-2xl bg-gradient-to-br from-amber-500/20 to-transparent border border-amber-400/20 p-5">
                    <div className="text-xs text-amber-200/80 uppercase tracking-wide">Current points</div>
                    <div className="mt-2 text-4xl font-black text-amber-300 tabular-nums">
                      {me?.openPoints?.points ?? 0}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      {sharePct.toFixed(2)}% of open pool ({me?.openPoints?.platformTotalPoints || 0} pts)
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <div className="text-xs text-slate-400 uppercase tracking-wide">Claimable now</div>
                    <div className="mt-2 text-3xl font-bold text-emerald-400 tabular-nums">
                      {formatUsdAmount(me?.claimable?.totalUsd || 0)}
                    </div>
                    <button
                      type="button"
                      disabled={!me?.claimable?.items?.length || !!claimingId}
                      onClick={claimAll}
                      className="mt-3 w-full rounded-xl bg-emerald-500 disabled:opacity-40 hover:bg-emerald-400 text-slate-950 font-bold text-sm py-2.5 transition"
                    >
                      {claimingId ? 'Claiming…' : 'Claim all'}
                    </button>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <div className="text-xs text-slate-400 uppercase tracking-wide">Next snapshot</div>
                    <div className="mt-2 text-sm font-semibold text-white leading-snug">
                      {fmtUtc(stats?.nextSnapshotAt)}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Hold through this moment to receive points. Selling before snapshot removes eligibility.
                    </p>
                  </div>
                </div>

                <h3 className="text-sm font-semibold text-slate-300 mb-3">Live NFT holdings (preview)</h3>
                <p className="text-xs text-slate-500 mb-4">
                  Live balances update when your wallet is linked. Points below become official only after the
                  snapshot check.
                </p>
                {(me?.live?.holdings || []).length === 0 ? (
                  <div className="rounded-xl border border-dashed border-white/15 px-4 py-8 text-center text-sm text-slate-500">
                    No eligible holdings detected on {shortAddr(me?.wallet)}.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {me.live.holdings.map((h) => (
                      <div
                        key={`${h.collectionId}-${h.contractAddress}`}
                        className="flex items-center gap-3 rounded-xl border border-white/10 bg-slate-950/50 p-4"
                      >
                        <div className="h-12 w-12 rounded-xl bg-amber-500/20 flex items-center justify-center text-amber-300 font-black">
                          {h.points}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-white truncate">{h.collectionName}</div>
                          <div className="text-xs text-slate-400">
                            Bal {h.balance}
                            {h.tierName ? ` · ${h.tierName}` : ''} · {h.points} pts (if snapshotted now)
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Claim list */}
              {(me?.claimable?.items || []).length > 0 && (
                <section className="rounded-3xl border border-emerald-500/20 bg-emerald-500/5 p-6 sm:p-8">
                  <h2 className="text-xl font-bold text-white mb-4">Ready to claim</h2>
                  <div className="space-y-3">
                    {me.claimable.items.map((item) => (
                      <div
                        key={String(item.allocationId)}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3"
                      >
                        <div>
                          <div className="font-semibold text-emerald-300">
                            {formatUsdAmount(item.amountUsd)} USDC
                          </div>
                          <div className="text-xs text-slate-400">
                            {item.label} · {item.points} pts · {fmtUtc(item.finalizedAt)}
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={!!claimingId}
                          onClick={() => handleClaim({ ...item, walletAddress: me.wallet })}
                          className="rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm px-4 py-2 disabled:opacity-40"
                        >
                          {claimingId === String(item.allocationId) ? 'Claiming…' : 'Claim'}
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Snapshot records */}
              <section className="rounded-3xl border border-white/10 bg-slate-900/60 p-6 sm:p-8">
                <h2 className="text-xl font-bold text-white mb-2">Snapshot records</h2>
                <p className="text-sm text-slate-400 mb-5">
                  Official points are granted only at these UTC moments. On-chain content hashes make the
                  record tamper-evident after admin commit.
                </p>
                {(me?.snapshots || []).length === 0 ? (
                  <p className="text-sm text-slate-500">No snapshots for this wallet yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                          <th className="py-2 pr-4">When</th>
                          <th className="py-2 pr-4">My pts</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2">Proof</th>
                        </tr>
                      </thead>
                      <tbody>
                        {me.snapshots.map((s) => (
                          <tr key={s._id} className="border-b border-white/5">
                            <td className="py-3 pr-4 text-slate-200 whitespace-nowrap">{fmtUtc(s.snapshotAt)}</td>
                            <td className="py-3 pr-4 font-semibold text-amber-300">{s.myPoints}</td>
                            <td className="py-3 pr-4">
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  s.status === 'included'
                                    ? 'bg-slate-700 text-slate-200'
                                    : s.status === 'committed'
                                      ? 'bg-emerald-500/20 text-emerald-300'
                                      : 'bg-amber-500/20 text-amber-200'
                                }`}
                              >
                                {s.status}
                              </span>
                            </td>
                            <td className="py-3 font-mono text-[11px] text-slate-500 truncate max-w-[12rem]">
                              {s.contentHash?.slice(0, 18)}…
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* History */}
              <section className="rounded-3xl border border-white/10 bg-slate-900/60 p-6 sm:p-8">
                <h2 className="text-xl font-bold text-white mb-4">Reward history</h2>
                {(me?.history || []).length === 0 ? (
                  <p className="text-sm text-slate-500">No distributions for this wallet yet.</p>
                ) : (
                  <div className="space-y-2">
                    {me.history.map((h, idx) => (
                      <div
                        key={`${h.distributionId}-${idx}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/5 bg-slate-950/40 px-4 py-3 text-sm"
                      >
                        <div>
                          <div className="font-medium text-white">{h.label}</div>
                          <div className="text-xs text-slate-500">{fmtUtc(h.finalizedAt)}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-amber-300">
                            {formatUsdAmount(h.amountUsd)}
                          </div>
                          <div className="text-xs text-slate-400">
                            {h.claimed ? 'Claimed' : 'Unclaimed'} · {h.points} pts
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Eligible collections */}
              <section className="rounded-3xl border border-white/10 bg-slate-900/60 p-6 sm:p-8">
                <h2 className="text-xl font-bold text-white mb-4">Eligible collections</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(stats?.collections || []).map((c) => (
                    <div key={c.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="font-semibold text-white">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-1 font-mono">{shortAddr(c.contractAddress)}</div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {(c.tiers || []).slice(0, 4).map((t) => (
                          <span
                            key={t.name}
                            className="rounded-full bg-amber-500/15 text-amber-200 text-[10px] font-semibold px-2 py-0.5"
                          >
                            {t.name} · {t.points}pt
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {!stats?.collections?.length && (
                    <p className="text-sm text-slate-500 col-span-full">No collections configured yet.</p>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reward;
